/* Carewell-style option selector, per owner's request — replaces the
   plain "Choose your Option" dropdown's visual presentation with:
   1) a closed-state trigger showing the currently selected option's
      name, its SKU underneath, and "See all N options" below that.
   2) clicking it opens a full-screen popup listing every option as a
      row (name, SKU, price), any of which can be clicked to select it.

   The stock <custom-select> markup stays fully intact in the DOM
   (hidden via CSS, not removed) — this only reads its existing
   .custom-select__option list items (which already carry
   data-value/data-variant-id from Shopify's own rendering) and
   forwards clicks to them, reusing 100% of Shopify's real
   variant-switching/price-update logic rather than reimplementing it.
   SKU per variant isn't available on those list items, so it's read
   separately from a small JSON lookup rendered alongside it in
   snippets/variant-picker.liquid.

   Real gap found: products with more than one option (e.g. Color +
   Corner Style + Thickness on a mattress cover) have one
   .option-selector per option — the original version here built one
   trigger+popup PER option-selector independently, so a 3-option
   product showed 3 separate stacked "See all N options" boxes, each
   only covering one dimension. Owner asked for a single combined
   entry point instead. Fixed by grouping option-selectors and
   building ONE trigger + ONE popup with a labelled section per option
   instead of one popup each. Single-option products (the common
   case, e.g. BetterDry's pack-size options) are unaffected — same
   one trigger/one flat list as before.

   Second, much deeper real bug found after that: owner reported a
   selected chip staying highlighted after picking a new one, and
   picking Style/Height after Color reverting back to whatever color
   was selected on page load. Root cause, confirmed directly: Shopify's
   own variant-picker.js does a full AJAX re-render of the ENTIRE
   variant-picker section on every option change — not just the
   option group that changed. Confirmed live (comparing object
   identity before/after a Height change): the Color group's own
   .option-selector, its <custom-select>, and even the <variant-picker>
   element itself all get replaced with brand new DOM nodes carrying
   freshly recomputed data-variant-id values. This project's own
   trigger/popup UI was built once and kept references to the
   ORIGINAL option/custom-select nodes — the instant any option
   changed, those references pointed at detached, stale markup,
   explaining both the frozen combined-variant summary bug (fixed
   earlier) and now this: clicking a Style/Height chip called
   selectOption() on a stale <custom-select> using a stale option
   whose data-variant-id still encoded the OLD (page-load) color.
   Also confirmed the earlier "ready" flag (set as a dataset property
   on the .option-selector itself) gets wiped every time too, since
   the flagged node is the one being replaced — meaning scan() kept
   re-detecting "new" unflagged option-selectors after every change
   and building ANOTHER duplicate trigger+popup (confirmed: 2 stray
   .llm-option-popup-overlay elements already existed in the DOM after
   a single option change in testing).

   Fixed by never caching option/custom-select DOM references past
   their immediate use — every read and every click re-resolves the
   CURRENT live nodes by their content (data-option name, data-value)
   rather than by object identity, scoped to the nearest
   `[id^="shopify-section-"]` wrapper (confirmed stable across these
   re-renders, unlike <variant-picker> or .option-selector). The
   "already built" flag now lives on that same stable wrapper instead
   of on the option-selector, so scan() no longer rebuilds duplicate
   UI after every option change.

   Third real bug, found via the quick-add drawer on a product page
   (not a collection/search page): the SKU/price JSON blocks
   (#llm-variant-sku-data / #llm-variant-price-data, rendered in
   snippets/variant-picker.liquid) use plain ids, but a product page
   already has its own copy of these for the main product — opening
   quick-add for a DIFFERENT product (e.g. from "You may also like")
   injects a second, duplicate-id copy for that product. A single,
   shared, page-wide skuMap/priceMap read via document.getElementById
   always resolves to the FIRST matching id in the document — the
   main product's own data, never the drawer's — so the popup for the
   second product showed every option correctly (that part reads live
   DOM nodes, unaffected) but with blank SKU/price text, since its
   variant ids were never keys in the wrong map. Confirmed directly:
   two #llm-variant-sku-data elements existed on the page, and the one
   document.getElementById returned belonged to the main product, not
   the one the drawer was showing. Fixed by scoping the sku/price
   lookup to each popup's own sectionRoot (searching sectionRoot itself
   for the shopify-section case, or its parent for the drawer case,
   where the data blocks are rendered as siblings of </variant-picker>
   rather than descendants of it) instead of a single shared,
   page-wide map — every function that needs sku/price data now lives
   inside buildGroup() itself so it closes over that one call's own,
   correctly-scoped maps rather than a shared outer variable. */
document.addEventListener('DOMContentLoaded', function () {
  /* Prices in the variant picker stay FLAT — no superscript cents (owner 2026-09-05: Amazon uses
     them "just in main pricing spots. Not on the variant cards"). This was briefly superscripted
     on 2026-09-04 and pulled back.

     Kept as a single helper rather than reverting to bare textContent at each of the eight sinks,
     so the decision lives in one place: if the picker should ever match the primary price again,
     this is the only function to change. */
  function setMoney(el, s) {
    el.textContent = s || '';
  }

  function optionValueOf(opt) {
    return opt.getAttribute('data-value') || opt.textContent.trim();
  }

  // Per-variant inventory + thresholds — same JSON the buy-box stock badge uses
  // (#llm-variant-inventory-data = { "<variantId>": {q, t}, ... }, #llm-inventory-config = {veryLow, low}).
  // Used to flag low-stock (but still in-stock) variant tiles with a "Low stock" line.
  // cardLow is the tile-specific threshold, SEPARATE from veryLow/low which drive the buy-box
  // badge + standalone stock badge — owner 2026-07-30 wanted the option cards to flag "Low stock"
  // only at <= 3, without changing the buy-box messaging (which stays on veryLow=6).
  var llmInvData = {}, llmInvCfg = { veryLow: 8, low: 20, cardLow: 3 };
  try {
    var _invEl = document.getElementById('llm-variant-inventory-data');
    if (_invEl) llmInvData = JSON.parse(_invEl.textContent || '{}');
    var _cfgEl = document.getElementById('llm-inventory-config');
    if (_cfgEl) {
      var _c = JSON.parse(_cfgEl.textContent || '{}');
      if (_c.veryLow) llmInvCfg.veryLow = _c.veryLow;
      if (_c.low) llmInvCfg.low = _c.low;
      if (_c.cardLow) llmInvCfg.cardLow = _c.cardLow;
    }
  } catch (e) {}
  function llmIsLowStock(variantId) {
    var d = variantId ? llmInvData[String(variantId)] : null;
    // Flag the option card only when stock is <= cardLow (3). This is intentionally tighter than
    // the buy box's veryLow (6) — owner: the cards should warn only on genuinely-scarce variants.
    return !!(d && d.t && d.q > 0 && d.q <= llmInvCfg.cardLow);
  }

  // Finds the #llm-variant-sku-data/#llm-variant-price-data belonging
  // to THIS sectionRoot specifically, not just whichever one happens
  // to be first in the document. The data blocks are rendered as
  // siblings immediately after </variant-picker> in
  // snippets/variant-picker.liquid, so they're descendants of a real
  // `[id^="shopify-section-"]` wrapper (search within sectionRoot
  // itself) but plain siblings of the bare <variant-picker> fallback
  // used inside the quick-add drawer (search within its parent).
  function readScopedDataMap(sectionRoot, id) {
    const scope = sectionRoot.querySelector('#' + id) ? sectionRoot : sectionRoot.parentElement;
    const el = scope ? scope.querySelector('#' + id) : null;
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return {};
    }
  }

  // Re-resolves the CURRENT live .option-selector for a given option
  // name (e.g. "Color"), scoped to sectionRoot. Never cache the
  // result past a single synchronous use — see file header.
  function findLiveSelector(sectionRoot, optionName) {
    const selectors = sectionRoot.querySelectorAll('.option-selector[data-selector-type="dropdown"]');
    for (let i = 0; i < selectors.length; i++) {
      if (selectors[i].getAttribute('data-option') === optionName) return selectors[i];
    }
    return null;
  }

  // Re-resolves the CURRENT live option + its <custom-select> for a
  // given (option name, option value) pair, e.g. ("Height", "16 inch
  // (+$20.00)"). This is the one function every click handler and
  // every refresh routine goes through — nothing else in this file
  // is allowed to hold onto an option/custom-select node across an
  // event boundary.
  function findLiveOption(sectionRoot, optionName, optionValue) {
    const selector = findLiveSelector(sectionRoot, optionName);
    if (!selector) return null;
    const opts = selector.querySelectorAll('.custom-select__option');
    for (let i = 0; i < opts.length; i++) {
      if (optionValueOf(opts[i]) === optionValue) {
        return { opt: opts[i], customSelectEl: selector.querySelector('custom-select') };
      }
    }
    return null;
  }

  // optionNames: e.g. ["Color", "Style", "Height"], in display order.
  // sectionRoot: the stable `[id^="shopify-section-"]` wrapper (or
  // best-available fallback) — never replaced by Shopify's own
  // re-renders, so this is the one thing safe to hold onto.
  function buildGroup(optionNames, sectionRoot, firstOptionSelector) {
    sectionRoot.dataset.llmPopupBuilt = 'true';

    // Scoped to THIS sectionRoot only — see file header for why this
    // can't be a single shared map read once at the top of the file.
    let skuMap = readScopedDataMap(sectionRoot, 'llm-variant-sku-data');
    let priceMap = readScopedDataMap(sectionRoot, 'llm-variant-price-data');
    let unitPriceMap = readScopedDataMap(sectionRoot, 'llm-variant-unit-price-data');
    /* Per-unit economics computed from the variant LABEL — see snippets/llm-variant-value-data.
       Preferred over unitPriceMap, which carries Shopify's unit_price and is the PACK price on the
       146-of-2,886 variants whose quantity_value is 1. */
    let valueMap = readScopedDataMap(sectionRoot, 'llm-variant-value-data');

    /* ONE place decides what any surface says about per-unit price, so the tiles and the popup rows
       can never disagree about the same variant. Returns '' when nothing trustworthy is known —
       showing no per-unit at all beats showing a pack price wearing an "/ea". */
    function unitTextFor(variantId) {
      const v = variantId && valueMap[variantId];
      /* A Single carries count 1 so it can serve as the BASELINE the case's saving is measured
         against (see the snippet) — but "$19.99" with "($19.99 / ea)" beneath it is the same
         number twice, so that figure stays in the data and out of the UI. Falling through to
         unitPriceMap would reintroduce it, so a known count of 1 returns '' outright. */
      if (v && v.per && v.count > 1) return v.per + '/ea';
      if (v && v.count === 1) return '';
      return variantId && unitPriceMap[variantId] ? unitPriceMap[variantId] : '';
    }

    function buildRow(optionName, optionValue, onSelect) {
      const live = findLiveOption(sectionRoot, optionName, optionValue);
      if (!live) return null;
      const opt = live.opt;
      const variantId = opt.getAttribute('data-variant-id');
      const sku = variantId && skuMap[variantId] ? skuMap[variantId] : '';
      // Real data found: Shopify/the stock custom-select already marks
      // genuinely sold-out variants with .is-unavailable (confirmed via
      // direct inspection). No separate JSON lookup needed, unlike
      // SKU/price.
      const isUnavailable = opt.classList.contains('is-unavailable');
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'llm-option-popup__row';
      if (opt.getAttribute('aria-selected') === 'true') {
        row.classList.add('is-selected');
      }
      if (isUnavailable) {
        row.classList.add('is-unavailable');
      }

      const rowName = document.createElement('span');
      rowName.className = 'llm-option-popup__row-name';
      rowName.textContent = optionValue;

      const rowSku = document.createElement('span');
      rowSku.className = 'llm-option-popup__row-sku';
      rowSku.textContent = sku ? ('SKU: ' + sku) : '';

      // Owner asked for SKU to sit on its own line under the option name
      // instead of sharing a row with it — grouping name+SKU into one
      // stacked column (with price staying on the right, its own
      // column) lets longer option names stay on a single line instead
      // of wrapping awkwardly next to the SKU/price text.
      const rowMain = document.createElement('span');
      rowMain.className = 'llm-option-popup__row-main';
      rowMain.appendChild(rowName);
      rowMain.appendChild(rowSku);

      // Owner asked to keep showing price for sold-out variants (not
      // replace it), with a separate "Out of stock" line underneath —
      // and to still let customers SELECT a sold-out variant (so the
      // Klaviyo "Notify me when available" trigger, which activates
      // based on which variant is currently selected, can show up). So
      // unlike the row's visual state, the click handler is never gated
      // off here.
      const rowPriceGroup = document.createElement('span');
      rowPriceGroup.className = 'llm-option-popup__row-price-group';

      const rowPrice = document.createElement('span');
      rowPrice.className = 'llm-option-popup__row-price';
      setMoney(rowPrice, variantId && priceMap[variantId] ? priceMap[variantId] : '');
      rowPriceGroup.appendChild(rowPrice);

      // Per-unit "$X.XX/ea" under the price (count packs only) — lets customers compare
      // per-unit across pack sizes right in the list.
      const rowUnitPrice = unitTextFor(variantId);
      if (rowUnitPrice) {
        const rowUnit = document.createElement('span');
        rowUnit.className = 'llm-option-popup__row-unit-price';
        setMoney(rowUnit, rowUnitPrice);
        rowPriceGroup.appendChild(rowUnit);
      }

      if (isUnavailable) {
        const rowOos = document.createElement('span');
        rowOos.className = 'llm-option-popup__row-oos';
        rowOos.textContent = 'Out of stock';
        rowPriceGroup.appendChild(rowOos);
      }

      row.appendChild(rowMain);
      row.appendChild(rowPriceGroup);

      row.addEventListener('click', function () {
        const freshLive = findLiveOption(sectionRoot, optionName, optionValue);
        if (freshLive) {
          if (freshLive.customSelectEl && typeof freshLive.customSelectEl.selectOption === 'function') {
            freshLive.customSelectEl.selectOption(freshLive.opt);
          } else {
            freshLive.opt.click();
          }
        }
        onSelect();
      });

      return row;
    }

    /* Owner asked to rethink the combined multi-option popup — showing
       a price on every one of 6+ color chips (etc.) per option group
       read as cluttered/confusing, and long option lists meant some
       option groups (e.g. Thickness, at the bottom) weren't visible
       without scrolling, risking a customer picking a color and style
       but never noticing/scrolling to Thickness and ordering the wrong
       one. Chips replace the old detailed rows for multi-option
       products only (single-option products, e.g. BetterDry's pack
       sizes, still use buildRow's full list — that's the customer's
       actual purchase decision there, so price-per-row still matters).
       Compact pill buttons (name only, no price/SKU) let every option
       group's full set of values usually fit without scrolling; the one
       price that actually matters — the full variant's — moves to a
       single summary line shared across all option groups instead. */
    function buildChip(optionName, optionValue, onSelect) {
      const live = findLiveOption(sectionRoot, optionName, optionValue);
      if (!live) return null;
      const opt = live.opt;
      const isUnavailable = opt.classList.contains('is-unavailable');
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'llm-option-popup__chip';
      if (opt.getAttribute('aria-selected') === 'true') {
        chip.classList.add('is-selected');
      }
      if (isUnavailable) {
        chip.classList.add('is-unavailable');
      }
      chip.textContent = optionValue;

      chip.addEventListener('click', function () {
        const freshLive = findLiveOption(sectionRoot, optionName, optionValue);
        if (freshLive) {
          if (freshLive.customSelectEl && typeof freshLive.customSelectEl.selectOption === 'function') {
            freshLive.customSelectEl.selectOption(freshLive.opt);
          } else {
            freshLive.opt.click();
          }
        }
        onSelect();
      });

      return chip;
    }

    /* Square option TILE — the same visual used by the inline buy-box grid (.llm-variant-tile):
       the option label split on "|" into stacked lines, plus price / unit price / Out-of-stock.
       Used both by the inline grid AND (owner request) by the single-option "+more options" popup,
       so the popup squares match the grid. Selecting re-resolves the CURRENT live option and calls
       Shopify's selectOption(), then runs onSelect (e.g. close the popup). */
    function buildTile(optionName, optionValue, onSelect) {
      const live = findLiveOption(sectionRoot, optionName, optionValue);
      if (!live) return null;
      const opt = live.opt;
      const variantId = opt.getAttribute('data-variant-id');
      const isUnavailable = opt.classList.contains('is-unavailable');
      const tile = document.createElement('button');
      tile.type = 'button';
      tile.className = 'llm-variant-tile';
      if (opt.getAttribute('aria-selected') === 'true') tile.classList.add('is-selected');
      if (isUnavailable) tile.classList.add('is-unavailable');

      const nm = document.createElement('span');
      nm.className = 'llm-variant-tile__name';
      optionValue.split('|').forEach(function (part) {
        const line = document.createElement('span');
        line.className = 'llm-variant-tile__name-line';
        line.textContent = part.trim();
        nm.appendChild(line);
      });
      tile.appendChild(nm);

      const priceText = variantId && priceMap[variantId] ? priceMap[variantId] : '';
      if (priceText) {
        const pr = document.createElement('span');
        pr.className = 'llm-variant-tile__price';
        setMoney(pr, priceText);
        tile.appendChild(pr);
      }
      /* BEST VALUE pill, overlaid on the tile's top border (owner, 2026-10-14). Only rendered when
         the Liquid map says this variant genuinely wins AND the saving cleared the 5% floor — the
         flag is not "cheapest per unit", because samples are subsidised and would otherwise take
         the badge off the case. All of that is decided in snippets/llm-variant-value-data.liquid;
         this is only the drawing.
         aria-hidden: the saving is already stated in the per-unit line right below, so a screen
         reader would otherwise hear the same claim twice. */
      const valueInfo = variantId && valueMap[variantId];
      if (valueInfo && valueInfo.best) {
        tile.classList.add('has-best-pill');
        const pill = document.createElement('span');
        pill.className = 'llm-variant-tile__pill';
        /* "Best value" ALONE. "Best value · save 5%" measured wider than the ~95px tile and spilled
           across its neighbour. The saving does not need stating here anyway — the per-unit price is
           two lines below and is the actual argument; the pill is just the flag pointing at it. */
        pill.textContent = 'Best value';
        pill.setAttribute('aria-hidden', 'true');
        tile.appendChild(pill);
      }

      const unitText = unitTextFor(variantId);
      if (unitText) {
        const u = document.createElement('span');
        u.className = 'llm-variant-tile__unit';
        /* Parenthesised, matching Amazon's "($4.25 / count)" (owner, 2026-09-21: "put the unit cost
           in parenthesis"). The source string is already money-formatted by variant-picker.liquid
           as "$4.25/ea", so this only adds the brackets and breathes out the slash.
           ⚠️ Done in JS, not with CSS ::before/::after. Generated content is not selectable, does
           not survive copy/paste, and is skipped or read inconsistently by screen readers — the
           brackets are part of the meaning here ("this is a per-unit price, not the price"), so
           they belong in the text. */
        setMoney(u, '(' + String(unitText).replace(/\s*\/\s*/, ' / ') + ')');
        tile.appendChild(u);
      }
      // SKU below the pricing (owner request 2026-07-29). skuMap is the same
      // scoped #llm-variant-sku-data map buildRow uses.
      const skuText = variantId && skuMap[variantId] ? skuMap[variantId] : '';
      if (skuText) {
        const sk = document.createElement('span');
        sk.className = 'llm-variant-tile__sku';
        sk.textContent = skuText;
        tile.appendChild(sk);
      }
      if (isUnavailable) {
        const oos = document.createElement('span');
        oos.className = 'llm-variant-tile__oos';
        oos.textContent = 'Out of stock';
        tile.appendChild(oos);
      } else if (llmIsLowStock(variantId)) {
        const low = document.createElement('span');
        low.className = 'llm-variant-tile__lowstock';
        var _lowQty = llmInvData[String(variantId)].q;
        low.textContent = 'Low stock (' + _lowQty + ')';
        tile.appendChild(low);
      }

      tile.addEventListener('click', function () {
        const fresh = findLiveOption(sectionRoot, optionName, optionValue);
        if (fresh) {
          if (fresh.customSelectEl && typeof fresh.customSelectEl.selectOption === 'function') {
            fresh.customSelectEl.selectOption(fresh.opt);
          } else {
            fresh.opt.click();
          }
        }
        if (typeof onSelect === 'function') onSelect();
      });
      return tile;
    }

    function currentSelectedText(optionName) {
      const selector = findLiveSelector(sectionRoot, optionName);
      if (!selector) return '';
      const opts = selector.querySelectorAll('.custom-select__option');
      const selected = selector.querySelector('.custom-select__option[aria-selected="true"]') || opts[0];
      return selected ? optionValueOf(selected) : '';
    }

    function currentSelectedSku(optionName) {
      const selector = findLiveSelector(sectionRoot, optionName);
      if (!selector) return '';
      const opts = selector.querySelectorAll('.custom-select__option');
      const selected = selector.querySelector('.custom-select__option[aria-selected="true"]') || opts[0];
      const variantId = selected ? selected.getAttribute('data-variant-id') : null;
      return variantId && skuMap[variantId] ? skuMap[variantId] : '';
    }

    const isMulti = optionNames.length > 1;

    // Amazon/Chewy-style inline tiles are used for SINGLE-option products on a real product
    // section only — NOT multi-option products (they keep the combined chip popup) and NOT the
    // quick-add drawer / any dialog (kept on the proven trigger+popup, which is compact for a
    // slideout). When false, everything below behaves exactly as before. Even if this gate were
    // wrong it can't break switching — tiles reuse the same live-option resolution as the popup.
    const useInlineTiles = !isMulti
      && typeof sectionRoot.id === 'string'
      && sectionRoot.id.indexOf('shopify-section-') === 0
      && !sectionRoot.closest('quick-add-drawer, .quick-add-drawer, .drawer, [data-quick-add], dialog');

    // Build the closed-state trigger.
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'llm-option-trigger';

    // Owner asked for a visible arrow signifying this is a
    // selector/dropdown, not just plain text — name + chevron share a
    // row so the arrow reads as "attached" to the selected value,
    // matching how a native select's own arrow is positioned.
    const triggerNameRow = document.createElement('span');
    triggerNameRow.className = 'llm-option-trigger__name-row';

    const triggerName = document.createElement('span');
    triggerName.className = 'llm-option-trigger__name';

    const triggerChevron = document.createElement('span');
    triggerChevron.className = 'llm-option-trigger__chevron';
    triggerChevron.innerHTML = '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    triggerNameRow.appendChild(triggerName);
    triggerNameRow.appendChild(triggerChevron);

    const triggerSku = document.createElement('span');
    triggerSku.className = 'llm-option-trigger__sku';

    const triggerSeeAll = document.createElement('span');
    triggerSeeAll.className = 'llm-option-trigger__see-all';
    if (isMulti) {
      triggerSeeAll.textContent = 'Choose options';
    } else {
      const firstOptions = firstOptionSelector.querySelectorAll('.custom-select__option');
      triggerSeeAll.textContent = 'See all ' + firstOptions.length + ' options';
    }

    trigger.appendChild(triggerNameRow);
    trigger.appendChild(triggerSku);
    trigger.appendChild(triggerSeeAll);

    // Owner: a visible "Choose your Option" label above the trigger. The stock label
    // lives inside the option-selector, which we visually-hide wholesale — so surface
    // our own here, above the trigger, and keep it with the trigger on every re-anchor.
    const triggerLabel = document.createElement('span');
    triggerLabel.className = 'llm-option-trigger-label';
    // Owner: show "<Option name>: <currently-selected value>" (e.g. "Option: MD (29\" to 43\") | Pack of
    // 15") instead of a static "Choose your Option", updating live as tiles/options are picked. Split
    // into name + value spans so the name reads as a quiet label and the value is emphasized.
    function updateTriggerLabel() {
      if (isMulti) { triggerLabel.textContent = 'Choose your options'; return; }
      triggerLabel.textContent = '';
      const _n = document.createElement('span');
      _n.className = 'llm-option-trigger-label__name';
      _n.textContent = optionNames[0] + ': ';
      const _v = document.createElement('span');
      _v.className = 'llm-option-trigger-label__value';
      _v.textContent = currentSelectedText(optionNames[0]);
      triggerLabel.appendChild(_n);
      triggerLabel.appendChild(_v);
      // Owner 2026-07-29: show the selected variant's SKU next to the option value,
      // above the tiles. Updates live via updateTrigger() on every option change.
      const _skuVal = currentSelectedSku(optionNames[0]);
      if (_skuVal) {
        const _s = document.createElement('span');
        _s.className = 'llm-option-trigger-label__sku';
        _s.textContent = 'SKU: ' + _skuVal;
        triggerLabel.appendChild(_s);
      }
    }
    updateTriggerLabel();

    function updateTrigger() {
      updateTriggerLabel();
      if (isMulti) {
        // Combine every option group's currently selected value into
        // one line (e.g. "Milky White (7 gauge) / Elastic Corners / 8
        // inch (standard)"). Owner asked for the SKU to also show here
        // — there's no single SKU per option group, only per full
        // variant, so triggerSku's text is set separately, from the
        // same on:variant:change-driven value already used for the
        // popup's own summary line (see below), not computed here.
        const parts = optionNames.map(function (name) {
          return currentSelectedText(name);
        });
        triggerName.textContent = parts.join(' / ');
      } else {
        triggerName.textContent = currentSelectedText(optionNames[0]);
        const sku = currentSelectedSku(optionNames[0]);
        triggerSku.textContent = sku ? ('SKU: ' + sku) : '';
      }
    }
    updateTrigger();

    // Build the popup overlay + panel.
    const overlay = document.createElement('div');
    overlay.className = 'llm-option-popup-overlay';

    const panel = document.createElement('div');
    panel.className = 'llm-option-popup';

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'llm-option-popup__close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '&times;';

    const header = document.createElement('div');
    header.className = 'llm-option-popup__header';

    const heading = document.createElement('div');
    heading.className = 'llm-option-popup__heading';
    heading.textContent = 'Choose an option';

    header.appendChild(heading);
    header.appendChild(closeBtn);

    function closePopup() {
      overlay.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    // Owner asked for the one price/SKU/availability that actually
    // matters — the full current variant, not a price per chip — to
    // live in a single summary line shared by every option group,
    // updating live as any group's selection changes. Sourced from
    // variant-picker.js's own `on:variant:change` event, which carries
    // the true current variant object regardless of which option
    // group triggered the change.
    let summaryPrice = null;
    let summaryUnitPrice = null;
    let summarySku = null;
    let summaryAvailability = null;
    if (isMulti) {
      const summary = document.createElement('div');
      summary.className = 'llm-option-popup__summary';

      summaryPrice = document.createElement('span');
      summaryPrice.className = 'llm-option-popup__summary-price';

      summaryUnitPrice = document.createElement('span');
      summaryUnitPrice.className = 'llm-option-popup__summary-unit-price';

      summarySku = document.createElement('span');
      summarySku.className = 'llm-option-popup__summary-sku';

      summaryAvailability = document.createElement('span');
      summaryAvailability.className = 'llm-option-popup__summary-availability';

      summary.appendChild(summaryPrice);
      summary.appendChild(summaryUnitPrice);
      summary.appendChild(summarySku);
      summary.appendChild(summaryAvailability);
      panel.appendChild(summary);

      document.addEventListener('on:variant:change', function (evt) {
        const variant = evt.detail && evt.detail.variant;
        if (!variant || !priceMap.hasOwnProperty(variant.id)) return;
        setMoney(summaryPrice, priceMap[variant.id] || '');
        if (summaryUnitPrice) setMoney(summaryUnitPrice, unitPriceMap[variant.id] || '');
        summarySku.textContent = variant.sku ? ('SKU: ' + variant.sku) : (skuMap[variant.id] ? 'SKU: ' + skuMap[variant.id] : '');
        // Owner asked for the trigger's own closed-state SKU line to
        // show for multi-option products too — reusing this same
        // event/value rather than computing it separately, since
        // there's no single option-group's own data-variant-id that
        // reliably reflects the full combined variant (see the
        // summary's own comment above for why).
        triggerSku.textContent = summarySku.textContent;
        const isUnavailable = !variant.available;
        summaryAvailability.textContent = isUnavailable ? 'Out of stock' : '';
        summaryAvailability.classList.toggle('is-unavailable', isUnavailable);
        updateTrigger();
        refreshAllSelected();
      });
    }

    // Every chip/row's highlight is re-synced here, on the SAME
    // on:variant:change handler above (so refreshing only ever
    // happens after Shopify's own variant state has genuinely
    // settled — its announceChange() is itself wrapped in a
    // setTimeout(..., 10), confirming this is asynchronous, not
    // instantaneous), by re-resolving each entry's live option node
    // fresh rather than reading a cached one.
    const allEntries = []; // {optionName, optionValue, el}
    function refreshAllSelected() {
      allEntries.forEach(function (entry) {
        const live = findLiveOption(sectionRoot, entry.optionName, entry.optionValue);
        entry.el.classList.toggle('is-selected', !!(live && live.opt.getAttribute('aria-selected') === 'true'));
      });
      // Only now does a tile actually carry .is-selected, so this is the earliest point the row can
      // be scrolled to it. No-op unless the selected tile is off-screen.
      ensureSelectedVisible();
    }

    function updateSummary() {
      if (!isMulti) return;
      const firstName = optionNames[0];
      const selector = findLiveSelector(sectionRoot, firstName);
      if (!selector) return;
      const opts = selector.querySelectorAll('.custom-select__option');
      const selected = selector.querySelector('.custom-select__option[aria-selected="true"]') || opts[0];
      if (!selected) return;
      const variantId = selected.getAttribute('data-variant-id');
      const isUnavailable = selected.classList.contains('is-unavailable');
      setMoney(summaryPrice, variantId && priceMap[variantId] ? priceMap[variantId] : '');
      if (summaryUnitPrice) setMoney(summaryUnitPrice, unitTextFor(variantId));
      const sku = variantId && skuMap[variantId] ? skuMap[variantId] : '';
      summarySku.textContent = sku ? ('SKU: ' + sku) : '';
      triggerSku.textContent = summarySku.textContent;
      summaryAvailability.textContent = isUnavailable ? 'Out of stock' : '';
      summaryAvailability.classList.toggle('is-unavailable', isUnavailable);
    }
    updateSummary();

    optionNames.forEach(function (optionName) {
      const selector = findLiveSelector(sectionRoot, optionName);
      if (!selector) return;
      const values = Array.from(selector.querySelectorAll('.custom-select__option')).map(optionValueOf);
      if (!values.length) return;

      if (isMulti) {
        const sectionHeading = document.createElement('div');
        sectionHeading.className = 'llm-option-popup__section-heading';
        sectionHeading.textContent = optionName;
        panel.appendChild(sectionHeading);
      }

      const list = document.createElement('div');
      // Multi-option keeps compact name-only chips; single-option now uses the same square tiles
      // as the inline buy-box grid (owner request), 2–3 per row (see llm-variant-tiles.css).
      list.className = isMulti ? 'llm-option-popup__chip-grid' : 'llm-option-popup__tile-grid';
      values.forEach(function (optionValue) {
        const onSelect = function () {
          if (!isMulti) closePopup();
          updateTrigger();
          updateSummary();
        };
        const el = isMulti
          ? buildChip(optionName, optionValue, onSelect)
          : buildTile(optionName, optionValue, onSelect);
        if (!el) return;
        allEntries.push({ optionName: optionName, optionValue: optionValue, el: el });
        list.appendChild(el);
      });
      panel.appendChild(list);
    });

    // Owner asked for an explicit way to close the popup once done
    // choosing — for multi-option products the popup deliberately
    // stays open after each selection (so a customer can pick color,
    // then style, then height in sequence), but that also means
    // there's no equivalent to the single-option popup's auto-close
    // on pick, and not every customer will think to tap the X or click
    // outside. Single-option products don't need this: picking a
    // value there already closes the popup immediately (see
    // onSelect above).
    if (isMulti) {
      const doneBtn = document.createElement('button');
      doneBtn.type = 'button';
      doneBtn.className = 'llm-option-popup__done';
      doneBtn.textContent = 'Done';
      doneBtn.addEventListener('click', closePopup);
      panel.appendChild(doneBtn);
    }

    panel.insertBefore(header, panel.firstChild);
    overlay.appendChild(panel);
    document.body.appendChild(overlay);

    function openPopup() {
      // Re-sync every tile/chip's is-selected highlight to the CURRENT live selection before
      // showing the popup. Fixes: on single-option products the popup's tiles were built once at
      // init and only refreshed on the isMulti-gated variant:change handler, so selecting via the
      // inline tiles left the popup still highlighting the build-time default (owner 2026-07-29).
      refreshAllSelected();
      overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }

    trigger.addEventListener('click', openPopup);
    closeBtn.addEventListener('click', closePopup);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closePopup();
    });

    /* ---- Amazon/Chewy-style inline variant tiles (single-option main PDP) ----
       Renders the option values as selectable tiles right in the buy box instead of a closed
       "See all N options" trigger. Each tile carries the option name + price + unit price + an
       Out-of-stock line, and selecting it re-resolves the CURRENT live option and calls Shopify's
       own selectOption() — identical plumbing to the popup rows, so price/URL/Add-to-Cart all
       update exactly as before. When there are more than TILE_CAP options, the last cell becomes a
       "+N more options" tile that opens the popup built above. tilesWrap stays null (and the
       classic trigger is used) whenever useInlineTiles is false. */
    let tilesWrap = null;
    // (Re)builds the inline tile grid from scratch to reflect the CURRENT selection. Called on
    // first build and again on every on:variant:change — Shopify re-renders the option markup and
    // the newly-selected value may be one that was in the overflow, so we recompute which values
    // are visible (the selected one is always shown) rather than just toggling a class. No-op when
    // inline tiles aren't in use (multi-option / quick-add), so the variant:change handler can call
    // it unconditionally.
    let renderTiles = function () {};
    // Faux always-visible scrollbar under the mobile card row (Amazon-style). Mobile touch browsers
    // ignore ::-webkit-scrollbar and show a fade-out overlay bar instead, so we draw + drive our own.
    // Declared at function scope so the insert/reattach code below (outside the useInlineTiles block)
    // can place it next to the tile row; updateScrollbar sizes/positions the thumb from scroll metrics.
    let scrollbarEl = null;
    let updateScrollbar = function () {};
    // Assigned inside the inline-tiles branch below. Declared out here — like updateScrollbar —
    // because refreshAllSelected() has to call it: that is what applies .is-selected, and it runs
    // AFTER renderTiles, so at render time no tile is marked selected yet.
    let ensureSelectedVisible = function () {};
    // Last user scroll position of the tile row, captured live on every scroll. Restored after each
    // rebuild so selecting an option doesn't snap the row back to the first cards. Captured HERE (on
    // scroll) rather than inside renderTiles because the variant-change re-render zeroes scrollLeft
    // before renderTiles runs — by then it's already lost.
    let lastScrollLeft = 0;
    if (useInlineTiles) {
      const name = optionNames[0];

      tilesWrap = document.createElement('div');
      tilesWrap.className = 'llm-variant-tiles';

      scrollbarEl = document.createElement('div');
      scrollbarEl.className = 'llm-variant-scrollbar';
      const scrollbarThumb = document.createElement('div');
      scrollbarThumb.className = 'llm-variant-scrollbar__thumb';
      // The thumb is decoration; the arrows below are real controls, so aria-hidden moved off the
      // track onto the thumb — inside an aria-hidden container the buttons would be invisible to
      // screen readers and unreachable by keyboard.
      scrollbarThumb.setAttribute('aria-hidden', 'true');
      scrollbarEl.appendChild(scrollbarThumb);

      /* Prev/next arrows at each end of the bar (owner, 2026-09-04), shown only between 500 and
         1000px — see llm-variant-tiles.css. Absolutely positioned OUTSIDE the track's horizontal
         bounds rather than added as siblings, so the insert/reattach code further down keeps working
         on a single element. */
      const mkArrow = function (dir) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'llm-variant-scrollbar__arrow llm-variant-scrollbar__arrow--' + dir;
        b.setAttribute('aria-label', dir === 'prev' ? 'Scroll options left' : 'Scroll options right');
        b.innerHTML = '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="'
          + (dir === 'prev' ? 'M10 3L5 8l5 5' : 'M6 3l5 5-5 5')
          + '" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        // The track listens for pointerdown to jump-scroll; without this a click on an arrow would
        // also be read as a click on the track underneath it and fight the scroll it just started.
        b.addEventListener('pointerdown', function (e) { e.stopPropagation(); });
        b.addEventListener('click', function (e) {
          e.stopPropagation();
          // One "page" less a tile, so the tile at the edge stays visible as an anchor.
          const step = Math.max(tilesWrap.clientWidth - 148, 148);
          const max = Math.max(0, tilesWrap.scrollWidth - tilesWrap.clientWidth);
          const target = tilesWrap.scrollLeft + (dir === 'prev' ? -step : step);
          /* Assigned directly, NOT scrollBy/scrollTo with behavior:'smooth'. This row sets
             scroll-snap-type:x, and a smooth programmatic scroll against it is silently dropped —
             verified live: behavior:'smooth' left scrollLeft at 0 across 8 samples while
             behavior:'auto' moved it. Snap still animates the landing, so it does not look abrupt. */
          tilesWrap.scrollLeft = Math.min(Math.max(target, 0), max);
          // Refresh thumb + arrow disabled states here rather than waiting for the scroll event.
          // Everything below drives scrollLeft programmatically, and a programmatic scroll's event
          // is not guaranteed to have fired by the time the next click lands — leaving "prev" stuck
          // disabled from init, so it silently did nothing.
          updateScrollbar();
        });
        return b;
      };
      const arrowPrev = mkArrow('prev');
      const arrowNext = mkArrow('next');
      scrollbarEl.appendChild(arrowPrev);
      scrollbarEl.appendChild(arrowNext);

      updateScrollbar = function () {
        const sw = tilesWrap.scrollWidth;
        const cw = tilesWrap.clientWidth;
        if (sw <= cw + 1) { scrollbarEl.style.display = 'none'; return; } // no overflow → no bar
        scrollbarEl.style.display = 'block';
        const thumbW = Math.max((cw / sw) * 100, 12); // % of track, floored so it stays grabbable-looking
        const maxScroll = sw - cw;
        const pos = maxScroll > 0 ? tilesWrap.scrollLeft / maxScroll : 0;
        scrollbarThumb.style.width = thumbW + '%';
        scrollbarThumb.style.left = (pos * (100 - thumbW)) + '%';
        // Grey out an arrow once there is nothing further that way. 1px of slack because
        // scrollLeft is fractional on zoomed/HiDPI displays and never lands exactly on maxScroll.
        arrowPrev.disabled = tilesWrap.scrollLeft <= 1;
        arrowNext.disabled = tilesWrap.scrollLeft >= maxScroll - 1;
      };
      /* Bring the SELECTED tile into view horizontally.
         The row starts at scrollLeft 0, so when the chosen variant is not among the first few tiles
         it is simply not on screen — on NorthShore MEGAMAX the first three tiles are the sold-out XS
         sizes, putting the selected "SM | Trial Pack of 4" at tile 4, measured 13px past the row's
         right edge. The customer sees an unhighlighted row and no indication anything is selected
         (owner 2026-09-06).

         ⚠️ scrollLeft ONLY — never scrollIntoView(). This runs during first paint and on every
         variant re-render; scrollIntoView would drag the whole PAGE vertically to the buy box each
         time, which is the same class of jump just fixed for the FBT picker.

         Minimal movement, and self-limiting: it does nothing when the tile is already visible. So a
         customer who has scrolled the row to browse is never yanked back — clicking a tile they can
         see cannot trigger it — while a selection changed from elsewhere (the popup, the FBT block,
         a ?variant= URL) is always brought into view. PEEK leaves a sliver of the previous tile
         showing so the row still reads as scrollable rather than as if it started there. */
      ensureSelectedVisible = function () {
        var sel = tilesWrap.querySelector('.llm-variant-tile.is-selected');
        if (!sel) return false;
        var max = tilesWrap.scrollWidth - tilesWrap.clientWidth;
        if (max <= 0) return false;                 // not laid out yet, or nothing to scroll
        var wrapR = tilesWrap.getBoundingClientRect();
        var selR = sel.getBoundingClientRect();
        if (selR.left >= wrapR.left - 1 && selR.right <= wrapR.right + 1) return true; // in view

        /* Align the tile's START to the row's start. That is NOT a stylistic choice — the row is
           `scroll-snap-type: x` with `scroll-snap-align: start` on each tile, so the only scroll
           offsets that survive are tile starts. An earlier version aimed for a 24px "peek" of the
           previous tile and the browser silently snapped it elsewhere: asking for 409 landed on 444,
           leaving the tile 5px past the right edge no matter how many times it retried. */
        var padLeft = parseFloat(getComputedStyle(tilesWrap).paddingLeft) || 0;
        var target = tilesWrap.scrollLeft + (selR.left - wrapR.left) - padLeft;
        tilesWrap.scrollLeft = Math.min(Math.max(target, 0), max);
        // Read BACK rather than storing the requested value — snapping adjusts it, and the restore
        // position has to match what the row is actually at.
        lastScrollLeft = tilesWrap.scrollLeft;
        /* Report whether the tile ACTUALLY ended up in view, not merely that the maths ran. The row
           reflows after this first fires — late webfonts and images change tile widths — and a
           one-shot alignment computed against the pre-reflow layout landed the tile 5px short of the
           right edge. Returning the real outcome lets the retry ladder correct itself. */
        var a = sel.getBoundingClientRect();
        var w = tilesWrap.getBoundingClientRect();
        return a.left >= w.left - 1 && a.right <= w.right + 1;
      };

      /* renderTiles() finishes before the row is laid out — tilesWrap is built and filled but is not
         yet in the document (it is inserted further down as `primaryAnchor`), so clientWidth is 0,
         scrollWidth - clientWidth is 0, and the alignment above has nothing to measure. A single rAF
         was not enough either. Retry on a short ladder and stop the moment it measures; each attempt
         is a no-op once the tile is in view, so overshooting costs nothing. */
      function alignSelectedWhenReady(tries) {
        if (ensureSelectedVisible()) return;
        if (tries <= 0) return;
        setTimeout(function () { alignSelectedWhenReady(tries - 1); }, 80);
      }

      tilesWrap.addEventListener('scroll', function () {
        lastScrollLeft = tilesWrap.scrollLeft;
        updateScrollbar();
      }, { passive: true });
      window.addEventListener('resize', updateScrollbar);

      /* MOUSE SUPPORT for the scroll row.
         This row used to exist only under 700px, i.e. touch devices, where swiping scrolls it and
         the bar below only had to INDICATE position. It now runs up to 1000px (owner, 2026-09-04),
         which covers plenty of mouse-driven windows — and there a mouse has no way to move it: the
         native scrollbar is hidden in CSS, the bar was decorative (aria-hidden, no handlers), and a
         vertical wheel does nothing to a horizontal overflow. Reported as "the scroll bar does
         nothing", correctly.

         Three additions, all pointer-based so touch keeps working unchanged:
           1. drag the thumb,
           2. click the track to jump,
           3. vertical wheel over the row scrolls it horizontally. */
      const scrollMax = () => Math.max(0, tilesWrap.scrollWidth - tilesWrap.clientWidth);

      // Track/thumb geometry: the thumb spans (100 - thumbW)% of the track, so a click at ratio r
      // of the TRACK maps to r of the scrollable distance, adjusted for the thumb's own width.
      const scrollToPointer = function (clientX) {
        const track = scrollbarEl.getBoundingClientRect();
        const thumb = scrollbarThumb.getBoundingClientRect();
        const usable = track.width - thumb.width;
        if (usable <= 0) return;
        const x = clientX - track.left - thumb.width / 2;
        tilesWrap.scrollLeft = Math.min(Math.max(x / usable, 0), 1) * scrollMax();
        updateScrollbar();          // see the note in the arrow click handler
      };

      let dragging = false;
      scrollbarEl.addEventListener('pointerdown', function (e) {
        dragging = true;
        scrollbarEl.setPointerCapture(e.pointerId);   // keeps tracking if the cursor leaves the bar
        scrollbarEl.classList.add('is-dragging');
        scrollToPointer(e.clientX);
        e.preventDefault();                            // no text selection while dragging
      });
      scrollbarEl.addEventListener('pointermove', function (e) {
        if (dragging) scrollToPointer(e.clientX);
      });
      const endDrag = function (e) {
        if (!dragging) return;
        dragging = false;
        scrollbarEl.classList.remove('is-dragging');
        try { scrollbarEl.releasePointerCapture(e.pointerId); } catch (err) {}
      };
      scrollbarEl.addEventListener('pointerup', endDrag);
      scrollbarEl.addEventListener('pointercancel', endDrag);

      // A trackpad already sends deltaX; only translate deltaY, and only while the row can actually
      // scroll that way — otherwise the page would stop scrolling vertically over the tiles.
      tilesWrap.addEventListener('wheel', function (e) {
        if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
        const max = scrollMax();
        if (!max) return;
        const next = tilesWrap.scrollLeft + e.deltaY;
        if ((next <= 0 && e.deltaY < 0) || (next >= max && e.deltaY > 0)) return;  // let the page take it at the ends
        tilesWrap.scrollLeft = next;
        updateScrollbar();          // see the note in the arrow click handler
        e.preventDefault();
      }, { passive: false });

      /* ===== DESKTOP PAGINATION (owner, 2026-09-21) ==========================================
         Modelled on Amazon's variant twister, measured on the PDP the owner linked
         (amazon.com/dp/B0D38J6DBM): SIX tiles per page in 2 rows of 3, ~93x96px each, with a
         numbered pager `< 1 [2] 3 4 >` underneath. 22 variants over 4 pages. Amazon has NO
         overflow-to-popup at all — the block never grows past two rows, you page through it.

         Owner: "our 3 rows is too much... ours just look gigantic and not as nice. I think people
         are more used to what Amazon shows. Maybe only show 4 or 5 pages max and then have the
         'Show all' card on the last page."

         ⚠️ PAGE SIZE IS MEASURED, NOT ASSUMED. It is (rendered column count x 2 rows). The CSS uses
         `auto-fill`, so the column count is whatever the PDP's buy-box column can hold — 4 here,
         3 in a narrower one. Hard-coding 6 to match Amazon's number would give two rows on THEIR
         layout and a ragged partial row on ours. Read the tracks, multiply by rows.
         Mobile (<1000px) is untouched: a horizontal scroller, where paging would be worse than
         swiping, keeps its 12-tile cap and "+N more" cell. */
      var PAGE_COLS = 3;          // usable columns; the 4th is the faded peek at the next page
      var PAGE_ROWS = 2;
      var MAX_PAGES = 5;          // beyond this the last cell becomes "Show all"
      var tilePage = null;        // null = open on the SELECTED variant's page, not page 1
      var pagerEl = null;
      var _refocusPager = false;  // set by a pager click; consumed by renderPager after the rebuild

      function tileGridMode() { return window.matchMedia('(min-width: 1000px)').matches; }

      /* auto-fill generates its track list from the container width even when the grid is empty,
         so this is safe to call before any tile is appended. */
      function gridColumns() {
        var t = getComputedStyle(tilesWrap).gridTemplateColumns;
        if (!t || t === 'none') return 4;
        var n = t.trim().split(/\s+/).filter(Boolean).length;
        return Math.max(1, Math.min(n, 8));
      }

      function moreTile(label, sub, hint) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'llm-variant-tile llm-variant-tile--more';
        b.innerHTML =
          '<span class="llm-variant-tile__more-count">' + label + '</span>' +
          '<span class="llm-variant-tile__more-label">' + sub + '</span>' +
          (hint ? '<span class="llm-variant-tile__more-hint">' + hint + '</span>' : '');
        b.addEventListener('click', openPopup);
        return b;
      }

      /* An empty grid cell. Invisible and untabbable, but it OCCUPIES a column — without it the
         next tile flows into the gap and the page silently reflows. See the interleave below. */
      function spacerCell() {
        var s = document.createElement('span');
        s.className = 'llm-variant-tile__spacer';
        s.setAttribute('aria-hidden', 'true');
        return s;
      }

      function renderPager(pages, current) {
        if (pagerEl) { pagerEl.remove(); pagerEl = null; }
        if (pages <= 1) return;
        var nav = document.createElement('nav');
        nav.className = 'llm-variant-pager';
        nav.setAttribute('aria-label', 'Variant pages');
        function btn(txt, cls, target, disabled, aria) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'llm-variant-pager__' + cls;
          b.textContent = txt;
          if (aria) b.setAttribute('aria-label', aria);
          if (disabled) { b.disabled = true; }
          else b.addEventListener('click', function () {
            tilePage = target;
            /* ⚠️ The pager is rebuilt by renderTiles(), so the button that was just clicked is
               DESTROYED mid-click. Focus falls back to <body>, and the browser scrolls to wherever
               that lands — the second half of the owner's "some page movement". Re-focus the
               equivalent button afterwards, with preventScroll so restoring focus does not itself
               scroll. The first half was the grid collapsing on a short page; see the 200px
               min-height in llm-variant-tiles.css. */
            _refocusPager = true;
            renderTiles();
          });
          return b;
        }
        nav.appendChild(btn('‹', 'arrow', current - 1, current === 0, 'Previous page'));
        for (var i = 0; i < pages; i++) {
          var p = btn(String(i + 1), 'page', i, false);
          if (i === current) { p.classList.add('is-current'); p.setAttribute('aria-current', 'true'); }
          nav.appendChild(p);
        }
        nav.appendChild(btn('›', 'arrow', current + 1, current === pages - 1, 'Next page'));
        /* ⚠️ APPENDED INSIDE THE GRID (spanning every column via CSS `grid-column: 1 / -1`), NOT as
           a sibling. The first version used insertAdjacentElement('afterend'), which silently did
           NOTHING: renderTiles() runs during build, BEFORE the theme inserts tilesWrap into the
           page, so there is no parent to insert next to and the pager never appeared. Living inside
           tilesWrap also means it survives every later move/reattach the theme does to the grid —
           a sibling would be orphaned by those. */
        tilesWrap.appendChild(nav);
        pagerEl = nav;
        if (_refocusPager) {
          _refocusPager = false;
          var cur = nav.querySelector('.llm-variant-pager__page.is-current');
          // preventScroll is the point of this — focus() without it scrolls the element into view,
          // which is the exact jump we are trying to remove.
          if (cur && cur.focus) { try { cur.focus({ preventScroll: true }); } catch (e) { /* older browsers */ } }
        }
      }

      renderTiles = function () {
        const liveSel = findLiveSelector(sectionRoot, name);
        const values = liveSel
          ? Array.from(liveSel.querySelectorAll('.custom-select__option')).map(optionValueOf)
          : [];
        if (!values.length) return;
        const selNode = liveSel.querySelector('.custom-select__option[aria-selected="true"]');
        const selValue = selNode ? optionValueOf(selNode) : null;

        const paged = tileGridMode();
        let slice, showAll = false, pages = 1, page = 0, shownCount = 0;

        if (paged) {
          /* THREE columns per page, not four. The grid renders FOUR 100px columns but the fourth is
             a faded preview of the next page (Amazon's trick — see llm-variant-tiles.css). So a
             page is 3 x 2 = 6 tiles, which is exactly Amazon's page size and why their 22 variants
             make 4 pages. gridColumns() is deliberately NOT used here: it would report 4 and treat
             the peek column as usable. */
          const pageSize = PAGE_COLS * PAGE_ROWS;
          const needed = Math.ceil(values.length / pageSize);
          const truncated = needed > MAX_PAGES;
          pages = truncated ? MAX_PAGES : needed;
          // One cell of the final page is spent on "Show all" when the list is longer than the cap,
          // so nothing is ever unreachable.
          const capacity = pages * pageSize - (truncated ? 1 : 0);
          shownCount = capacity;          // used by the "+N more options" count below
          const shown = values.slice(0, capacity);
          // The selected variant must always be visible somewhere, even if it sits past the cap.
          if (selValue && shown.indexOf(selValue) === -1) shown[shown.length - 1] = selValue;

          page = tilePage;
          if (page === null) {
            const si = selValue ? shown.indexOf(selValue) : -1;
            page = si >= 0 ? Math.floor(si / pageSize) : 0;   // open where the selection lives
          }
          page = Math.max(0, Math.min(page, pages - 1));
          tilePage = page;

          slice = shown.slice(page * pageSize, page * pageSize + pageSize);
          showAll = truncated && page === pages - 1;
        } else {
          // Mobile scroller — unchanged behaviour.
          const TILE_CAP = 12;
          const overflow = values.length > TILE_CAP;
          const visibleCount = overflow ? TILE_CAP - 1 : values.length;
          slice = values.slice(0, visibleCount);
          if (overflow && selValue && slice.indexOf(selValue) === -1) slice[slice.length - 1] = selValue;
          showAll = overflow;
        }

        tilesWrap.textContent = '';

        function tileFor(v, isPeek) {
          // Shared tile builder (defined above). on:variant:change re-renders the grid, so the
          // per-tile onSelect only needs to refresh the closed-trigger text.
          const t = buildTile(name, v, function () { updateTrigger(); });
          if (t && isPeek) {
            t.classList.add('llm-variant-tile--peek');
            /* ⚠️ FULLY SELECTABLE — not decoration (owner, 2026-09-21: "why can't a customer select
               the half cards in the 4th column? They should still be able to select those if they
               are shown enough to be able to see what they are").

               He is right and my first version was wrong: I made these `pointer-events: none` and
               aria-hidden on the theory that a half-faded tile is unreadable. But it IS readable —
               the name and price sit in the opaque left portion — and a customer who can read
               "MD (32\" to 44\")" and reaches for it should not find it dead. Showing something
               that looks like a control and refusing the click is worse than either hiding it or
               letting it work.

               Clicking one jumps to the page it belongs to, so the newly selected tile is shown
               fully and in context instead of staying half-faded at the edge. Registered in the
               CAPTURE phase so the page advances before buildTile's own handler triggers the
               variant change and the re-render that follows it. */
            t.addEventListener('click', function () { tilePage = page + 1; }, true);
          }
          return t;
        }

        if (paged) {
          /* ROW-MAJOR WITH THE PEEK INTERLEAVED. The grid is 4 columns and fills left-to-right, so
             to land the page's 6 tiles in columns 1-3 and the preview in column 4 the append order
             has to be:  p0 p1 p2 [peek0]  p3 p4 p5 [peek1]
             Appending the peeks at the END instead would push them onto a third row, where the fade
             would look like a rendering bug rather than a hint.

             ⚠️ Amazon gets this for free because it lays out ALL variants in one wide grid and
             translates it; we render only the current page, so the peek has to be stitched in by
             hand. Keep the interleave and the CSS column count (4) in step. */
          /* ⚠️ EVERY CELL MUST BE APPENDED, INCLUDING THE EMPTY ONES.
             The grid is 4 columns and flows row-major, so a cell left unfilled does not stay
             empty — the next element slides into it. Skipping the append for a missing tile, or
             for the peek on the last page, silently reflows the rest of the page.

             Owner, 2026-09-21: "why is the XL Sample variant card not in the 2nd row like it
             should be?" — on the LAST page there is no next page, so no peek tile was appended,
             and the 4th variant flowed up into row 1 column 4 where the container clipped it in
             half. Row 2 then rendered only two tiles.

             So: build the page's cells first, then walk rows × 4 and append a tile OR an inert
             spacer for every single cell. Position stops depending on how many tiles happen to
             exist. */
          const cells = [];
          slice.forEach(function (v) {
            const t = tileFor(v, false);
            if (t) cells.push(t);
          });
          if (showAll) {
            /* The "+N more options" card is the last CELL of the page, not an extra appended after
               the grid — appended after, it landed on a phantom third row. capacity already
               reserved its slot (`- (truncated ? 1 : 0)`), so it lands in the final column 1-3
               position exactly where a variant would have. */
            cells.push(moreTile('+' + Math.max(0, values.length - shownCount), 'more options', 'Click to view'));
          }

          const peek = values.slice((page + 1) * (PAGE_COLS * PAGE_ROWS));
          let peekIdx = 0;
          for (let row = 0; row < PAGE_ROWS; row++) {
            for (let col = 0; col < PAGE_COLS; col++) {
              const c = cells[row * PAGE_COLS + col];
              tilesWrap.appendChild(c || spacerCell());
            }
            // Column 4: the next page's tile, faded — or a spacer holding the column open.
            const pv = showAll ? undefined : peek[peekIdx];
            const pt = pv === undefined ? null : tileFor(pv, true);
            tilesWrap.appendChild(pt || spacerCell());
            peekIdx++;
          }
        } else {
          slice.forEach(function (v) {
            const t = tileFor(v, false);
            if (t) tilesWrap.appendChild(t);
          });
        }

        if (showAll && !paged) {
          /* ⚠️ SAME CARD IN BOTH MODES. The paged branch briefly rendered this as "Show / all 26",
             which put the word "Show" in the slot the big count belongs in and dropped the
             "Click to view" hint — owner, 2026-09-21: "the show all card looks different than it
             did before, I liked it better before."
             The original three-part shape — big "+N", "more options", red "Click to view" — is what
             tells a customer both HOW MANY are hidden and that the card is clickable. Keep it. */
          const hidden = Math.max(0, values.length - (paged ? shownCount : slice.length));
          tilesWrap.appendChild(moreTile('+' + hidden, 'more options', 'Click to view'));
        }

        if (paged) {
          /* ⚠️ RESET scrollLeft. `overflow: hidden` on the grid makes it a SCROLL CONTAINER — it
             just has no visible scrollbar. The browser quietly scrolls it to bring the selected
             tile into view, which shifted the whole grid left by 2px and clipped the first
             column's left border (owner: "the left hand side of the first card in the top and
             bottom row look like they still have 1px cutoff"). It read like a padding problem and
             was not one — the padding was fine, the content had moved under it.
             The paged grid never scrolls by design, so pinning this to 0 is always correct. */
          tilesWrap.scrollLeft = 0;
          renderPager(pages, page);
          return;                                          // grid: no scroller, no scrollbar
        }

        if (pagerEl) { pagerEl.remove(); pagerEl = null; }
        // Restore the pre-rebuild scroll position (captured live on scroll). Re-assert on the next
        // frame too, since Shopify's variant-change re-render can zero it again just after this runs.
        var _sl = lastScrollLeft;
        tilesWrap.scrollLeft = _sl;
        if (window.requestAnimationFrame) requestAnimationFrame(function () { tilesWrap.scrollLeft = _sl; });
        alignSelectedWhenReady(12);
        updateScrollbar();
      };

      /* TILE_CAP now has THREE bands (12 / 6 / 9), not two, so watching only the 749px boundary
         would leave a stale cap on the 1000px and 1200px crossings — e.g. dragging a window from
         1150 to 1250 would keep the 2-column cap of 6 while CSS switched to 3 columns, rendering
         "+N more" in the middle of a row that had space. Key off the cap itself rather than any one
         media query: it is the value that actually matters, and it cannot drift out of sync with
         the bands above. */
      /* Crossing 1000px swaps the whole layout (paged grid <-> horizontal scroller) AND changes the
         column count, so the current page index is meaningless on the other side. Reset it to null
         so the next render re-opens on the SELECTED variant's page rather than a stale index. */
      var _tileBand = function () {
        return window.matchMedia('(min-width: 1000px)').matches ? 'grid' : 'scroller';
      };
      var _lastTileBand = _tileBand();
      window.addEventListener('resize', function () {
        var b = _tileBand();
        if (b !== _lastTileBand) { _lastTileBand = b; tilePage = null; renderTiles(); }
      });
      renderTiles();
    }

    // The primary in-buy-box element: the inline tile grid when enabled, otherwise the classic
    // closed trigger. Everything below (hide native selectors, re-attach across Shopify's
    // re-renders, insert into the DOM) operates on this so single-option-tiles and the
    // multi/quick-add trigger share the exact same lifecycle handling.
    const primaryAnchor = tilesWrap || trigger;

    // Hiding the CURRENT live option-selectors here is enough for
    // first paint, but not for every re-render after that — Shopify
    // replaces this markup wholesale on every option change, and the
    // fresh replacement nodes won't carry this class. That's fine:
    // llm-option-popup.css also hides
    // `.option-selector[data-selector-type="dropdown"]`
    // unconditionally (not gated behind this class), so every
    // replacement node is hidden automatically without needing to
    // re-apply anything here.
    function hideLiveSelectors() {
      optionNames.forEach(function (optionName) {
        const selector = findLiveSelector(sectionRoot, optionName);
        if (selector) selector.classList.add('llm-option-selector-hidden');
      });
    }
    hideLiveSelectors();

    // Real bug found in testing: the trigger itself (unlike the
    // popup, which lives safely in document.body) was inserted as a
    // sibling of the ORIGINAL option-selector, inside its original
    // parent. Since Shopify replaces that entire parent subtree on
    // every option change, the trigger got discarded along with it —
    // confirmed live: after any variant change, .llm-option-trigger
    // count dropped to 0 and the buy box showed no way to reopen the
    // popup at all, even though the popup itself kept working
    // correctly once opened. Re-anchoring the trigger next to
    // whichever option-selector is CURRENTLY live, every time
    // on:variant:change fires, keeps it permanently visible across
    // however many times Shopify swaps the underlying markup.
    function reattachTrigger() {
      if (primaryAnchor.isConnected) return;
      const liveFirst = findLiveSelector(sectionRoot, optionNames[0]);
      if (liveFirst && liveFirst.parentElement) {
        liveFirst.parentElement.insertBefore(primaryAnchor, liveFirst);
        primaryAnchor.parentElement.insertBefore(triggerLabel, primaryAnchor);
        if (scrollbarEl) { primaryAnchor.insertAdjacentElement('afterend', scrollbarEl); updateScrollbar(); }
      }
    }

    document.addEventListener('on:variant:change', function (evt) {
      const variant = evt.detail && evt.detail.variant;
      if (!variant || !priceMap.hasOwnProperty(variant.id)) return;
      hideLiveSelectors();
      reattachTrigger();
      // Rebuild the inline tiles after Shopify's own variant state settles (no-op when tiles
      // aren't in use). This re-derives which options are visible + their selected state; it reads
      // live option nodes and never re-clicks anything, so it can't loop.
      renderTiles();
    });

    firstOptionSelector.parentElement.insertBefore(primaryAnchor, firstOptionSelector);
    primaryAnchor.parentElement.insertBefore(triggerLabel, primaryAnchor);
    if (scrollbarEl) { primaryAnchor.insertAdjacentElement('afterend', scrollbarEl); updateScrollbar(); }
  }

  function scan() {
    const candidates = document.querySelectorAll('.option-selector[data-selector-type="dropdown"]');
    const groups = new Map(); // sectionRoot -> [optionSelector, ...]
    candidates.forEach(function (sel) {
      const sectionRoot = sel.closest('[id^="shopify-section-"]') || sel.closest('variant-picker') || sel.parentElement;
      if (sectionRoot.dataset.llmPopupBuilt) return;
      if (!groups.has(sectionRoot)) groups.set(sectionRoot, []);
      groups.get(sectionRoot).push(sel);
    });
    groups.forEach(function (selectors, sectionRoot) {
      if (!selectors.length) return;
      const optionNames = selectors.map(function (s) { return s.getAttribute('data-option') || ''; });
      buildGroup(optionNames, sectionRoot, selectors[0]);
    });
  }
  scan();
  new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
});
